import React, { useContext, useState } from 'react';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { SHOW_HEADER_NOTIFICATIONS } from '../../../constants/headerNotifications';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { SidebarContext } from '../../contexts/SidebarContext';
import { HEADER_SLOT_ID } from './headerSlot';
import { useAuth } from '../../hooks/useAuth';
import navigationConfig from '../../config/navigation';
import LogoutConfirmDialog from '../../../components/common/LogoutConfirmDialog';
import { palette, typography, radius, size, borderWidth } from 'theme/tokens';

/**
 * The breadcrumb trail and title for a route — the same shape the partner
 * header builds (`partner/config/breadcrumbs.js`): `Home / Section / …`, with
 * every crumb that is a real page clickable, and on a detail route the record
 * id from the URL as the title ("Home / Bookings / JC1WVLX9K").
 */
const HOME = { label: 'Home', to: '/admin' };

function getPageInfo(pathname) {
  const path = pathname.replace(/\/+$/, '') || '/admin';
  if (path === '/admin') return { crumbs: [], title: 'Dashboard' };
  if (path === '/admin/profile') return { crumbs: [HOME], title: 'Profile' };

  let best = null;
  for (const group of navigationConfig) {
    for (const item of group.items) {
      const parentCrumb = { label: item.label, to: item.path || null };
      if (item.path === path) return { crumbs: [HOME], title: item.label };
      for (const child of item.children || []) {
        if (child.path === path) {
          return { crumbs: parentCrumb.label === child.label ? [HOME] : [HOME, parentCrumb], title: child.label };
        }
      }
      // A detail route (/admin/bookings/:id) has no nav entry of its own —
      // it sits under the longest nav path it starts with.
      for (const entry of [item, ...(item.children || [])]) {
        if (
          entry.path &&
          entry.path !== '/admin' &&
          path.startsWith(`${entry.path}/`) &&
          (!best || entry.path.length > best.entry.path.length)
        ) {
          best = { entry, parentCrumb: entry === item ? null : parentCrumb };
        }
      }
    }
  }
  if (best) {
    const crumbs = [HOME];
    if (best.parentCrumb && best.parentCrumb.label !== best.entry.label) crumbs.push(best.parentCrumb);
    crumbs.push({ label: best.entry.label, to: best.entry.path });
    const id = decodeURIComponent(path.slice(best.entry.path.length + 1).split('/')[0]);
    return { crumbs, title: id || best.entry.label };
  }
  return { crumbs: [HOME], title: 'Page' };
}

export default function Header() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { toggleMobile } = useContext(SidebarContext);
  const { user, logout } = useAuth();
  const [confirmLogout, setConfirmLogout] = useState(false);
  const location = useLocation();
  const { crumbs, title } = getPageInfo(location.pathname);
  const navigate = useNavigate();
  const [profileAnchor, setProfileAnchor] = useState(null);
  const closeProfile = () => setProfileAnchor(null);

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: palette.white,
        borderBottom: `1px solid ${palette.neutral[70]}`,
        zIndex: theme.zIndex.appBar,
      }}
    >
      <Toolbar sx={{ minHeight: 64, px: { xs: 2, sm: 3 } }}>
        {isMobile && (
          <IconButton
            edge="start"
            onClick={toggleMobile}
            sx={{ mr: 1, color: theme.palette.text.primary }}
          >
            <MenuIcon />
          </IconButton>
        )}

        <Box sx={{ flexGrow: 1 }}>
          {crumbs.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'nowrap', overflow: 'hidden' }}>
              {crumbs.map((c) => (
                <Box key={c.label + (c.to || '')} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                  <Typography
                    component={c.to ? Link : 'span'}
                    to={c.to || undefined}
                    variant="body2"
                    color={c.to ? 'primary' : 'text.secondary'}
                    sx={{ textDecoration: 'none', whiteSpace: 'nowrap', '&:hover': c.to ? { textDecoration: 'underline' } : undefined }}
                  >
                    {c.label}
                  </Typography>
                  <Typography variant="body2" color="text.disabled">/</Typography>
                </Box>
              ))}
            </Box>
          )}
          <Typography
            variant="h4"
            sx={{
              color: theme.palette.text.primary,
              mt: crumbs.length > 0 ? 0.25 : 0,
              lineHeight: typography.lineHeight.tight,
            }}
          >
            {title}
          </Typography>
        </Box>

        {/* Pages portal a compact control in here — see headerSlot.js. Empty
            (and zero-width) everywhere else. */}
        <Box
          id={HEADER_SLOT_ID}
          sx={{ display: 'flex', alignItems: 'center', minWidth: 0, mr: 2, '&:empty': { display: 'none' } }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {SHOW_HEADER_NOTIFICATIONS && (
            <>
              <Tooltip title="Notifications">
                <IconButton
                  sx={{
                    color: palette.neutral[470],
                    '&:hover': { backgroundColor: palette.neutral[40] },
                  }}
                >
                  <Badge
                    // SMB-4215: was a hardcoded placeholder count. There is no notifications
                    // API yet, so 0 — which MUI hides — until a real unread count is wired in.
                    badgeContent={0}
                    color="primary"
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: typography.fontSize[10],
                        minWidth: 18,
                        height: 18,
                      },
                    }}
                  >
                    <NotificationsNoneIcon sx={{ fontSize: typography.fontSize[22] }} />
                  </Badge>
                </IconButton>
              </Tooltip>

              <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 1.5 }} />
            </>
          )}

          {/* The identity block opens a small menu with the Profile entry —
              the same pattern as the partner header. */}
          <Box
            onClick={(e) => setProfileAnchor(e.currentTarget)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              pl: 0.5,
              pr: 0.5,
              py: 0.5,
              borderRadius: '10px',
              cursor: 'pointer',
              '&:hover': { backgroundColor: palette.neutral[40] },
            }}
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,
                backgroundColor: theme.palette.primary.main,
                fontSize: typography.fontSize[14],
                fontWeight: typography.fontWeight.bold,
              }}
            >
              {user?.name?.charAt(0) || 'A'}
            </Avatar>
            {!isMobile && (
              <Typography
                variant="body2"
                sx={{ fontWeight: typography.fontWeight.semiBold, color: theme.palette.text.primary, lineHeight: typography.lineHeight.tight, fontSize: typography.fontSize[12] }}
              >
                {user?.name}
              </Typography>
            )}
          </Box>

          <Menu
            anchorEl={profileAnchor}
            open={Boolean(profileAnchor)}
            onClose={closeProfile}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                sx: {
                  minWidth: size[160],
                  mt: 1,
                  borderRadius: `${radius.xl}px`,
                  border: `${borderWidth.thin} solid ${palette.neutral[70]}`,
                },
              },
            }}
            MenuListProps={{ sx: { py: 1 } }}
          >
            <MenuItem
              onClick={() => {
                closeProfile();
                navigate('/admin/profile');
              }}
              sx={{ mx: 1, px: 1, py: 1, gap: 1.5, borderRadius: `${radius.md}px`, fontSize: typography.fontSize[12], color: palette.neutral[670], '&:hover, &.Mui-focusVisible': { backgroundColor: palette.neutral[40] } }}
            >
              <PersonOutlineIcon fontSize="small" sx={{ color: palette.tw.gray[500] }} /> Profile
            </MenuItem>
          </Menu>

          <Tooltip title="Logout">
            <IconButton
              onClick={() => setConfirmLogout(true)}
              sx={{
                color: palette.neutral[330],
                ml: 0.5,
                '&:hover': { backgroundColor: palette.custom.red[30], color: palette.mui.red[700] },
              }}
            >
              <LogoutIcon sx={{ fontSize: typography.fontSize[20] }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>

      <LogoutConfirmDialog
        open={confirmLogout}
        onCancel={() => setConfirmLogout(false)}
        onConfirm={() => {
          setConfirmLogout(false);
          logout();
        }}
      />
    </AppBar>
  );
}
